import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchParkingTypeDetails } from "../../../api/parkingReport";
import { fetchAllParkingTypes } from "../../../api/parkingTypes";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

function ParkingTypeReport() {
  const [parkingType, setParkingType] = useState("");
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parkingTypes, setParkingTypes] = useState([]);

  const tenantId = localStorage.getItem("tenantId");
  const locationId = localStorage.getItem("locationId");

  // Function to format date as dd-MM-yyyy
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch parking types on component mount
  useEffect(() => {
    // Set default dates to today
    const today = dayjs(); // Default input format for date fields
    setFromDate(today);
    setToDate(today);

    const getParkingType = async () => {
      try {
        const response = await fetchAllParkingTypes(locationId, tenantId);
        // Add "All" option to the parking types
        setParkingTypes([{ id: "all", name: "All" }, ...response]);
      } catch (err) {
        console.error("Failed to fetch parking types:", err);
      }
    };

    getParkingType();
  }, [locationId, tenantId]);

  // Memoized handleSearch function
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (parkingType === "all") {
        // Fetch data for all parking types
        response = await fetchParkingTypeDetails(
          locationId,
          tenantId,
          fromDate.format("YYYY-MM-DD"),
          toDate.format("YYYY-MM-DD"),
          ""
        );
      } else {
        // Fetch data for the selected parking type
        response = await fetchParkingTypeDetails(
          locationId,
          tenantId,
          fromDate.format("YYYY-MM-DD"),
          toDate.format("YYYY-MM-DD"),
          parkingType
        );
      }
      setReportData(response?.parkingData || response?.data || []);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [locationId, tenantId, fromDate, toDate, parkingType]);

  // Automatically fetch data when the component mounts or when dates/parkingType change
  useEffect(() => {
    if (fromDate && toDate) {
      handleSearch();
    }
  }, [fromDate, toDate, parkingType, handleSearch]);

  // Calculate grand totals
  const grandTotalCount = reportData.reduce(
    (sum, row) => sum + (row.totalCount || 0),
    0
  );
  const grandTotalAmount = reportData.reduce(
    (sum, row) => sum + (row.totalAmount || 0),
    0
  );

  // Export to Excel
  const exportToExcel = () => {
    // Add serial numbers to the data
    const dataWithSerialNumbers = reportData.map((row, index) => ({
      No: index + 1, // Serial number
      Date: formatDate(row.date), // Format date as dd-MM-yyyy
      "Parking Type": row.parkingTypename,
      "Vehicle Type": row.vehicleTypename,
      Count: row.totalCount,
      Amount: row.totalAmount,
    }));

    // Add grand total row
    const grandTotalRow = {
      No: "", // Leave serial number blank for the grand total row
      Date: "",
      "Parking Type": "",
      "Vehicle Type": "Grand Total",
      Count: grandTotalCount,
      Amount: grandTotalAmount,
    };

    // Combine data with serial numbers and the grand total row
    const dataForExport = [...dataWithSerialNumbers, grandTotalRow];

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parking Type Report");

    // Export to Excel
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "parking_type_report.xlsx");
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Typography className="text-[#252F40] !mb-2">
          <Link to="/dashboard" className="no-underline">
            <span className="text-[#8C8C8C]">Reports / </span>
          </Link>
          Parking Type
        </Typography>

        <Box
          sx={{
            background: "white",
            borderRadius: "10px",
            boxShadow: "0px 3.75px 7.5px rgba(0, 0, 0, 0.12)",
          }}
          className="flex items-center mb-3 p-5"
          gap={2}
        >
          <Box className="flex flex-wrap gap-2 items-center">
            {/* <div>
            <TextField
              required
              name="fromDate"
              type="date"
              label="From Date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              focused
            />
          </div>
          <div>
            <TextField
              required
              name="toDate"
              type="date"
              label="To Date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              focused
            />
          </div> */}
            <DatePicker
              required
              format="DD-MM-YYYY"
              id="outlined-required"
              label="From Date"
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              onChange={(val) => setFromDate(val)}
              value={fromDate ?? null}
            />

            <DatePicker
              required
              format="DD-MM-YYYY"
              id="outlined-required"
              minDate={fromDate}
              label="To Date"
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              onChange={(val) => setToDate(val)}
              value={toDate ?? null}
            />
            <div>
              <FormControl size="small" variant="outlined">
                <InputLabel htmlFor="parking-type">Parking Type</InputLabel>
                <Select
                  required
                  labelId="parking-type"
                  id="parking-type"
                  label="Parking Type*"
                  className="!min-w-[300px]"
                  name="parkingTypeId"
                  value={parkingType}
                  onChange={(e) => setParkingType(e.target.value)}
                >
                  {parkingTypes?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* <Button
              name="search"
              sx={{
                background: "#576782",
                color: "white",
                "&:hover": { background: "#3e4859" },
              }}
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? "Loading..." : "Search"}
            </Button> */}

            {/* Export to Excel Button */}
            <Button
              variant="outlined"
              onClick={exportToExcel}
              disabled={reportData.length === 0}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>

        {error && <Typography color="error">{error}</Typography>}

        <Card>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#576782" }}>
                  <TableCell sx={{ color: "white" }}>No</TableCell>
                  <TableCell sx={{ color: "white" }}>Date</TableCell>
                  <TableCell sx={{ color: "white" }}>Parking Type</TableCell>
                  <TableCell sx={{ color: "white" }}>Vehicle Type</TableCell>
                  <TableCell sx={{ color: "white" }}>Count</TableCell>
                  <TableCell sx={{ color: "white" }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              {reportData.length > 0 ? (
                <TableBody>
                  {reportData.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatDate(row.date)}</TableCell>{" "}
                      {/* Format date as dd-MM-yyyy */}
                      <TableCell>{row.parkingTypename}</TableCell>
                      <TableCell>{row.vehicleTypename}</TableCell>
                      <TableCell>{row.totalCount}</TableCell>
                      <TableCell>{row.totalAmount}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell colSpan={4} sx={{ fontWeight: "bold" }}>
                      Grand Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {grandTotalCount}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {grandTotalAmount}
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Card>
      </LocalizationProvider>
    </>
  );
}

export default ParkingTypeReport;
